import { useMemo } from "react";
import type { TemplateVariable } from "../lib/schema-paths.js";
import { flattenSchema } from "../lib/schema-paths.js";
import { getManifestCache } from "./useActivityManifest.js";

interface Edge {
	source: string;
	target: string;
}

interface Node {
	id: string;
	data: { stepType?: string };
}

/** Walk the DAG backwards from stepId to find all upstream step IDs */
export function getUpstreamStepIds(stepId: string, edges: Edge[]): string[] {
	const result: string[] = [];
	const visited = new Set<string>();
	const queue = [stepId];

	while (queue.length > 0) {
		const current = queue.shift();
		if (current === undefined) break;
		for (const edge of edges) {
			if (edge.target === current && !visited.has(edge.source)) {
				visited.add(edge.source);
				result.push(edge.source);
				queue.push(edge.source);
			}
		}
	}

	return result;
}

/**
 * Hook that returns template variables from all upstream steps.
 * Accesses the manifest cache directly (populated when nodes are clicked).
 * Graceful degradation: if a manifest isn't cached yet, that step's outputs are omitted.
 */
export function useUpstreamOutputs(
	stepId: string | undefined,
	nodes: Node[],
	edges: Edge[],
): TemplateVariable[] {
	const upstreamIds = useMemo(
		() => (stepId ? getUpstreamStepIds(stepId, edges) : []),
		[stepId, edges],
	);

	return useMemo(() => {
		const variables: TemplateVariable[] = [];
		const cache = getManifestCache();

		for (const id of upstreamIds) {
			const node = nodes.find((n) => n.id === id);
			if (!node?.data?.stepType) continue;

			const manifest = cache.get(node.data.stepType);
			if (!manifest?.output_schema) continue;

			variables.push(...flattenSchema(`steps.${id}.output`, manifest.output_schema));
		}

		variables.push({ path: "inputs", type: "object", description: "Workflow inputs" });
		return variables;
	}, [upstreamIds, nodes]);
}
